import { serialize } from "object-to-formdata"

/**
 * Converts form values → serialized API payload for steps that use pure form state.
 *
 * Steps with non-form local state (scheduling, media, location, resources, digital_waiver)
 * register their own save handlers via stepSaveHandlersRef — this file only handles
 * the steps where form values alone are sufficient.
 *
 * IMPORTANT: API keys here must match existing backend contract exactly.
 */

/**
 * Details step payload.
 * Maps multi-value string arrays to the _attributes format expected by Rails.
 * selectedLanguages / selectedSkillLevels come from ExperienceDetails callbacks
 * (those components don't use Form.Item onChange, they use prop callbacks).
 */
export const buildDetailsPayload = (formValues, { selectedLanguages = [], selectedSkillLevels = [] } = {}) => {
  const { categories = [], included_items = [], bring_items = [], ...rest } = formValues
  return serialize(
    {
      trip: {
        ...rest,
        // Raw arrays (matches old module — backend uses these for display/filtering)
        categories,
        included_items,
        bring_items,
        price_per_person: 10,
        languages: selectedLanguages,
        skill_levels: selectedSkillLevels,
        // _attributes format (Rails associations for create/update)
        trip_categories_attributes: categories.map((name) => ({ name })),
        trip_included_items_attributes: included_items.map((name) => ({ name })),
        trip_bring_items_attributes: bring_items.map((name) => ({ name }))
      }
    },
    { allowEmptyArrays: true }
  )
}

/**
 * Preview step payload — marks the wizard as complete.
 */
export const buildPreviewPayload = () =>
  serialize({ trip: { wizard_completed: true } }, { allowEmptyArrays: true })

/**
 * Scheduling step payload builder (used inside SchedulingStep's registered handler).
 * Extracted here for testability and co-location with other payload builders.
 */
export const buildSchedulingPayload = (formValues, { isSeasonalExperience, timeBlocks, localPrices = [] }) => {
  const updateData = {
    time_zone: formValues.time_zone,
    available_days:
      formValues.available_days && typeof formValues.available_days === "object"
        ? Object.keys(formValues.available_days).reduce((acc, day) => {
            acc[day] = !!formValues.available_days[day]
            return acc
          }, {})
        : formValues.available_days,
    pricing_type: formValues.pricing_type,
    full_payment_required: formValues.full_payment_required,
    deposit_percentage: formValues.deposit_percentage,
    seasonal: isSeasonalExperience,
    available_hours: (formValues.available_hours || []).map(({ start, end }) => ({
      start: start?.format?.("HH:mm") ?? start,
      end: end?.format?.("HH:mm") ?? end
    })),
    trip_slots_attributes: (formValues.available_hours || []).map(({ start, end }) => ({
      start_time: start?.format?.("HH:mm") ?? start,
      end_time: end?.format?.("HH:mm") ?? end
    }))
  }

  if (isSeasonalExperience) {
    updateData.recurring_yearly = formValues.recurringYearly

    const dateRanges = timeBlocks
      .map((blockIndex) => {
        const start = formValues[`seasonStart_${blockIndex}`]
        const end = formValues[`seasonEnd_${blockIndex}`]
        if (start && end) {
          return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") }
        }
        return null
      })
      .filter(Boolean)

    const seasonalTripsAttributes = timeBlocks
      .map((blockIndex) => {
        const start = formValues[`seasonStart_${blockIndex}`]
        const end = formValues[`seasonEnd_${blockIndex}`]
        if (start && end) {
          return { start_date: start.format("YYYY-MM-DD"), end_date: end.format("YYYY-MM-DD") }
        }
        return null
      })
      .filter(Boolean)

    if (dateRanges.length > 0) {
      updateData.available_date_ranges = dateRanges
      updateData.seasonal_trips_attributes = seasonalTripsAttributes
    }
  }

  const trip_prices_attributes = localPrices
    .filter((p) => p._destroy || p.pricing_type)
    .map((p) => {
      if (p._destroy) return { id: p.id, _destroy: true }
      const { tempId, ...data } = p
      return data
    })

  return serialize(
    { trip: { ...formValues, ...updateData, trip_prices_attributes } },
    { allowEmptyArrays: true }
  )
}

/**
 * Location step payload builder (used inside LocationStep's registered handler).
 */
export const buildLocationPayload = ({ additional_directions, meetingLatLng, coordinates }) =>
  serialize(
    {
      trip: {
        additional_directions: additional_directions || "",
        latitude: coordinates?.lat,
        longitude: coordinates?.lng,
        address: meetingLatLng
      }
    },
    { allowEmptyArrays: true }
  )

/**
 * Resources step payload builder (used inside ResourcesStep's registered handler).
 */
export const buildResourcesPayload = ({ selectedEquipment, selectedCrewMembers, autoAssignEquipment }) =>
  serialize(
    {
      trip: {
        boat_ids: selectedEquipment.filter((id) => id !== "all").map(Number),
        assigned_staff_ids: selectedCrewMembers.filter((id) => id !== "all").map(Number),
        auto_assign_equipment: autoAssignEquipment
      }
    },
    { allowEmptyArrays: true }
  )

/**
 * Media step payload builder (used inside MediaStep's registered handler).
 */
export const buildMediaPayload = (localMedia) =>
  serialize(
    {
      trip: {
        media_attributes: localMedia.map(({ id, file_type, blob, _destroy, position, file }) => ({
          file_type,
          _destroy,
          position,
          ...(file ? { file } : { id }),
          ...(file_type === "video" || file_type === "trimmed_video" ? { preview: blob } : {})
        }))
      }
    },
    { indices: true }
  )
